import { createSelector } from '@reduxjs/toolkit'

import type { QueryResultLike } from '@krp/client-runtime'

import { connectionVm } from '../../../shared/model/connection'
import type { CommandFeedback } from '../../../shared/model/feedback'
import { formatPrice } from '../../../shared/model/format'
import { formatMoment, makeQueryViewSelector, readyData } from '../../../shared/model/query'
import { makeSelectSourceCaption } from '../../source/model/selectors'
import type { AqsiSource } from '../../source/model/types'
import {
  CATALOG_PAGE_SIZE,
  type AqsiCashiers,
  type AqsiDevice,
  type AqsiDevices,
  type AqsiFiscalCheck,
  type AqsiGoodsPage,
  type AqsiMaterialization,
  type AqsiProbeStatus,
  type CatalogRidIntent,
  type CatalogVm,
  type DeviceRowVm,
  type GoodRowVm,
} from './types'

export interface CatalogVmInput {
  resourceId: string
  sources: QueryResultLike<readonly AqsiSource[]>
  goods: QueryResultLike<AqsiGoodsPage>
  devices: QueryResultLike<AqsiDevices>
  cashiers: QueryResultLike<AqsiCashiers>
  check: QueryResultLike<AqsiFiscalCheck>
  intent: CatalogRidIntent
  feedback: CommandFeedback | undefined
}

/** Возраст снимка словами: на полку и в чек уходит то, что в снимке, — время видно всегда. */
export function snapshotText(materialization: AqsiMaterialization | null): string {
  return materialization === null
    ? 'Снимок кабинета ещё не снимался — нажмите «Загрузить … из кабинета»'
    : `Снимок на ${formatMoment(materialization.completedAt)}: ${materialization.count} записей`
}

/** Строка состояния индекса над поиском: всегда видна, как свежо то, по чему ищем. */
export function probeStatusLine(status: AqsiProbeStatus | null): { text: string; kind: 'ok' | 'wait' | 'none' | 'failed' } {
  if (status === null) return { text: 'Состояние индекса не получено', kind: 'none' }
  switch (status.state) {
    case 'ready':
      return { text: `Индекс по снимку от ${formatMoment(status.snapshotAt)}, ${status.count} карточек`, kind: 'ok' }
    case 'building':
      return { text: 'Строится…', kind: 'wait' }
    case 'failed':
      return { text: `Ошибка построения: ${status.error ?? 'причина не передана'}`, kind: 'failed' }
    case 'none':
      return status.latestSnapshotAt === null
        ? { text: 'Индекса нет: снимок ещё не выгружен — нажмите «Загрузить товары из кабинета»', kind: 'none' }
        : { text: 'Индекса нет — нажмите «Перестроить индекс»', kind: 'none' }
  }
}

export function presenceOf(missingSince: string | null, invalidReason: string | null) {
  if (invalidReason !== null) return { state: `не по формату: ${invalidReason}`, stateKind: 'invalid' as const }
  if (missingSince !== null)
    return { state: `пропал из кабинета ${formatMoment(missingSince)}`, stateKind: 'missing' as const }
  return { state: 'в кабинете', stateKind: 'ok' as const }
}

/** СНО — битовые коды ФФД (тег 1055), как их проверяет служба (`AqsiFiscalVerifier.KnownTaxSystems`). */
const TAX_SYSTEMS: Readonly<Record<number, string>> = {
  1: 'ОСН',
  2: 'УСН доход',
  4: 'УСН доход − расход',
  8: 'ЕНВД',
  16: 'ЕСХН',
  32: 'Патент',
}

/** Строка кассы для экрана: каталог и структура кабинета называют кассу одними словами. */
export function deviceRowOf(device: AqsiDevice): DeviceRowVm {
  return {
    deviceId: device.deviceId,
    serial: device.serialNumber,
    model: device.model ?? '—',
    shop: device.shopName ?? '—',
    taxSystem:
      device.receiptTaxSystem === null
        ? 'СНО не указана'
        : // Источник значения — служебный ключ (`aqsi.cabinet.v4…`): человеку важно, что оно из настроек кассы в кабинете.
          `${TAX_SYSTEMS[device.receiptTaxSystem] ?? `код ${device.receiptTaxSystem}`} (из настроек кассы в кабинете)`,
    ...presenceOf(device.missingSince, device.invalidReason),
    connection: connectionVm(device.connection),
  }
}

export const makeSelectCatalogVm = () => {
  const selectSource = makeSelectSourceCaption()
  const selectGoodsView = makeQueryViewSelector(
    (input: CatalogVmInput) => input.goods,
    (data) => data.items.length === 0,
  )
  const selectDevicesView = makeQueryViewSelector(
    (input: CatalogVmInput) => input.devices,
    (data) => data.items.length === 0,
  )
  const selectCashiersView = makeQueryViewSelector(
    (input: CatalogVmInput) => input.cashiers,
    (data) => data.items.length === 0,
  )
  const selectGoods = createSelector(
    [
      selectGoodsView,
      (input: CatalogVmInput) => input.goods.data,
      (input: CatalogVmInput) => input.intent.checkGoodIds,
    ],
    (view, data, checked): readonly GoodRowVm[] =>
      (readyData(view, data)?.items ?? []).map((good) => ({
        goodId: good.goodId,
        name: good.name,
        sku: good.sku ?? '—',
        price: formatPrice(good.price),
        barcodes: good.barcodes.join(', ') || '—',
        ...presenceOf(good.missingSince, good.invalidReason),
        checked: checked.includes(good.goodId),
      })),
  )
  const selectDevices = createSelector(
    [selectDevicesView, (input: CatalogVmInput) => input.devices.data],
    (view, data): readonly DeviceRowVm[] =>
      (readyData(view, data)?.items ?? []).map(deviceRowOf),
  )
  const selectCashiers = createSelector(
    [selectCashiersView, (input: CatalogVmInput) => input.cashiers.data],
    (view, data) =>
      (readyData(view, data)?.items ?? []).map((cashier) => ({
        id: cashier.cashierId,
        name: cashier.name,
        state:
          cashier.missingSince !== null ? `пропал из кабинета ${formatMoment(cashier.missingSince)}` : 'в кабинете',
      })),
  )
  const selectCheck = createSelector(
    [
      selectDevices,
      (input: CatalogVmInput) => input.intent,
      (input: CatalogVmInput) => input.check.data,
      (input: CatalogVmInput) => input.check.isLoading,
      (input: CatalogVmInput) => input.feedback?.phase === 'sending',
    ],
    (devices, intent, result, checking, busy): CatalogVm['check'] => {
      const blockedReason =
        intent.checkDeviceId === null
          ? 'Выберите кассу'
          : intent.checkGoodIds.length === 0
            ? 'Отметьте товары для проверки'
            : null
      return {
        deviceId: intent.checkDeviceId,
        deviceOptions: [
          { value: '', label: 'Касса не выбрана' },
          ...devices
            .filter((device) => device.stateKind === 'ok')
            .map((device) => ({ value: String(device.deviceId), label: `${device.serial} · ${device.shop}` })),
        ],
        goodCount: intent.checkGoodIds.length,
        canRun: blockedReason === null && !checking && !busy,
        blockedReason,
        result:
          result === undefined
            ? null
            : {
                ready: result.ready,
                title: result.ready
                  ? 'Можно фискализировать: профиль проверен по снимкам кабинета'
                  : 'Фискализировать нельзя',
                lines: result.ready
                  ? result.profile !== null
                    ? [
                        `СНО: ${TAX_SYSTEMS[result.profile.taxSystem] ?? `код ${result.profile.taxSystem}`}`,
                        `ИНН организации: ${result.profile.legalEntityInn}`,
                        `Строк: ${result.profile.lines}, проверено ${formatMoment(result.profile.checkedAt)}`,
                      ]
                    : []
                  : result.blocks.map((block) =>
                      block.goodId !== null ? `${block.goodId}: ${block.message}` : block.message,
                    ),
              },
      }
    },
  )
  return createSelector(
    [
      (input: CatalogVmInput) => input.resourceId,
      (input: CatalogVmInput) =>
        selectSource({ purpose: 'catalog', resourceId: input.resourceId, query: input.sources }),
      selectGoodsView,
      selectGoods,
      (input: CatalogVmInput) => input.intent.page,
      (input: CatalogVmInput) => input.goods.data,
      selectDevicesView,
      selectDevices,
      (input: CatalogVmInput) => input.devices.data,
      selectCashiersView,
      selectCashiers,
      selectCheck,
      (input: CatalogVmInput) => input.feedback ?? null,
    ],
    (
      resourceId,
      source,
      goodsQuery,
      goods,
      page,
      goodsData,
      devicesQuery,
      devices,
      devicesData,
      cashiersQuery,
      cashiers,
      check,
      feedback,
    ): CatalogVm => {
      const total = goodsData?.total ?? 0
      return {
        resourceId,
        source,
        goodsQuery,
        goods,
        page,
        pageCount: Math.max(1, Math.ceil(total / CATALOG_PAGE_SIZE)),
        total,
        goodsSnapshot: goodsData !== undefined ? snapshotText(goodsData.materialization) : '',
        devicesQuery,
        devices,
        devicesSnapshot: devicesData !== undefined ? snapshotText(devicesData.materialization) : '',
        cashiersQuery,
        cashiers,
        check,
        busy: feedback?.phase === 'sending' ? feedback.command : null,
        feedback,
      }
    },
  )
}
